import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Clock, Database, ExternalLink, RefreshCw, Server, type LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import { LoadingState, PageHeader } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';

type Status = 'healthy' | 'degraded' | 'unhealthy';

interface HealthCheck {
  status: Status;
  latencyMs?: number;
  error?: string;
}

interface HealthStatus {
  status: Status;
  checks: Partial<Record<string, HealthCheck>>;
  version: string;
  uptime: number;
  timestamp: string;
}

/** Origin of the API server (health endpoints live outside /api/v1). Empty = same origin. */
function getApiOrigin(): string {
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
  try {
    return new URL(apiUrl).origin;
  } catch {
    return '';
  }
}

const statusStyles: Record<Status | 'unknown', { panel: string; dot: string; badge: string }> = {
  healthy: { panel: 'border-success-200 bg-success-50 text-success-800', dot: 'bg-success-500', badge: 'badge-success' },
  degraded: { panel: 'border-warning-200 bg-warning-50 text-warning-800', dot: 'bg-warning-500', badge: 'badge-warning' },
  unhealthy: { panel: 'border-danger-200 bg-danger-50 text-danger-800', dot: 'bg-danger-500', badge: 'badge-danger' },
  unknown: { panel: 'border-gray-200 bg-gray-50 text-gray-700', dot: 'bg-gray-400', badge: 'badge-gray' },
};

const styleFor = (status?: string) => statusStyles[(status as Status) in statusStyles ? (status as Status) : 'unknown'];

const checkIcons: Record<string, LucideIcon> = { database: Database, redis: Server };

const ENDPOINTS = [
  { path: '/health', key: 'full' },
  { path: '/health/live', key: 'live' },
  { path: '/health/ready', key: 'ready' },
  { path: '/metrics', key: 'metrics' },
] as const;

export function SystemHealth() {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const apiOrigin = getApiOrigin();

  const { data: health, refetch, isLoading, isFetching, error } = useQuery({
    queryKey: ['system-health'],
    queryFn: async (): Promise<HealthStatus> => {
      const response = await fetch(`${apiOrigin}/health`);
      // The API answers 503 with a full JSON body when a dependency is unhealthy:
      // show those details instead of a generic "API down" error.
      if (!response.ok && response.status !== 503) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as HealthStatus;
      if (!body || typeof body.status !== 'string') throw new Error('Invalid health payload');
      return body;
    },
    refetchInterval: autoRefresh ? 10000 : false,
  });

  const statusLabel = (status?: string) => t(`admin.health.status.${status}`, { defaultValue: status ?? '—' });

  const formatUptime = (totalSeconds: number): string => {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const parts: string[] = [];
    if (days > 0) parts.push(t('admin.health.units.d', { count: days }));
    if (hours > 0) parts.push(t('admin.health.units.h', { count: hours }));
    if (minutes > 0) parts.push(t('admin.health.units.m', { count: minutes }));
    parts.push(t('admin.health.units.s', { count: secs }));
    return parts.join(' ');
  };

  const checks = Object.entries(health?.checks ?? {}).filter(
    (entry): entry is [string, HealthCheck] => Boolean(entry[1])
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('nav.systemHealth')}
        description={t('admin.health.description')}
        actions={
          <>
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {t('admin.health.autoRefresh')}
            </label>
            <button type="button" onClick={() => refetch()} disabled={isFetching} className="btn-secondary">
              <RefreshCw className={clsx('h-4 w-4', isFetching && 'animate-spin')} />
              {t('admin.health.refresh')}
            </button>
          </>
        }
      />

      {error && (
        <div role="alert" className="alert-danger">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">{t('admin.health.fetchFailed')}</p>
            {health && <p className="mt-0.5 text-xs">{t('admin.health.showingLastKnown')}</p>}
          </div>
        </div>
      )}

      {isLoading && <LoadingState />}

      {health && (
        <>
          {/* Overall status */}
          <div className={clsx('rounded-xl border p-5 sm:p-6', styleFor(health.status).panel)}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="relative flex h-3.5 w-3.5">
                  <span
                    className={clsx(
                      'absolute inline-flex h-full w-full animate-ping rounded-full opacity-60',
                      styleFor(health.status).dot
                    )}
                  />
                  <span className={clsx('relative inline-flex h-3.5 w-3.5 rounded-full', styleFor(health.status).dot)} />
                </span>
                <div>
                  <h2 className="text-lg font-semibold">
                    {t('admin.health.overall', { status: statusLabel(health.status) })}
                  </h2>
                  <p className="text-sm opacity-80">
                    {t('admin.health.lastChecked')}{' '}
                    {health.timestamp ? new Date(health.timestamp).toLocaleString(locale) : '—'}
                  </p>
                </div>
              </div>
              <div className="sm:text-end">
                <p className="text-xs uppercase tracking-wider opacity-70">{t('admin.health.version')}</p>
                <p className="font-mono font-semibold">{health.version ?? '—'}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Uptime */}
            <div className="card p-5">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                <Clock className="h-4 w-4" />
                {t('admin.health.uptime')}
              </div>
              <p className="mt-2 font-mono text-2xl font-semibold text-gray-900">{formatUptime(health.uptime ?? 0)}</p>
              <p className="mt-1 text-xs text-gray-500">
                {t('admin.health.since')}{' '}
                {new Date(Date.now() - (health.uptime ?? 0) * 1000).toLocaleString(locale)}
              </p>
            </div>

            {/* Dependency checks */}
            {checks.map(([name, check]) => {
              const Icon = checkIcons[name] ?? Server;
              const style = styleFor(check.status);
              return (
                <div key={name} className="card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
                      <Icon className="h-4 w-4" />
                      {t(`admin.health.checks.${name}`, { defaultValue: name })}
                    </div>
                    <span className={style.badge}>
                      <span className={clsx('h-1.5 w-1.5 rounded-full', style.dot)} />
                      {statusLabel(check.status)}
                    </span>
                  </div>
                  <p className="mt-2 font-mono text-2xl font-semibold text-gray-900">
                    {check.latencyMs !== undefined ? (
                      <>
                        {check.latencyMs}
                        <span className="ms-1 text-sm font-normal text-gray-500">ms</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{t('admin.health.latency')}</p>
                  {check.error && (
                    <p className="mt-3 break-words rounded-lg bg-danger-50 p-2.5 text-xs text-danger-800">
                      <strong>{t('admin.health.error')}:</strong> {check.error}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Endpoints */}
          <div className="card overflow-hidden p-0">
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="card-title">{t('admin.health.endpoints')}</h3>
            </div>
            <ul className="divide-y divide-gray-100">
              {ENDPOINTS.map((endpoint) => (
                <li key={endpoint.path} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <code dir="ltr" className="font-mono text-sm text-primary-700">
                      GET {endpoint.path}
                    </code>
                    <p className="text-xs text-gray-500">{t(`admin.health.endpointDesc.${endpoint.key}`)}</p>
                  </div>
                  <a
                    href={`${apiOrigin}${endpoint.path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost btn-sm shrink-0"
                  >
                    {t('admin.health.open')}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
